/**
 * Connect — Auto-Compiler Proxy PLG Onboarding (60-second flow)
 *
 * The conversion surface for the Auto-Compiler proxy.
 *
 * Steps:
 *   1. Demo a sample prompt — show live compaction in browser (no signup needed)
 *   2. Show what they'd save per month at their volume
 *   3. CTA: sign up + create proxy key
 *
 * Designed to convert a curious developer in <60 seconds with zero friction.
 */
import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ENGINE_COUNT } from '../constants/product';
import {
  Sparkles, Zap, Database, Scissors, GitBranch, ArrowRight, Code2,
  Calculator, Lock, CheckCircle2,
} from 'lucide-react';

const SAMPLE_PROMPT = `You are a helpful customer support assistant for ACME Corp.
You are a helpful customer support assistant for ACME Corp.



<!-- agent note: be concise -->

The user's name is John Doe.    They have an enterprise account.
The user's name is John Doe. They have an enterprise account.

Question:   What is the refund policy?  `;

// Mirror of the server-side heuristic compactor — runs entirely client-side.
function clientCompact(text) {
  if (!text) return text;
  let t = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  t = t.replace(/<!--[\s\S]*?-->/g, '');
  t = t.replace(/\n{3,}/g, '\n\n');
  t = t.replace(/[ \t]{2,}/g, ' ');
  t = t.replace(/[ \t]+(\n|$)/g, '$1');
  if (t.length >= 25) {
    const parts = t.split(/(?<=[.!?])\s+/);
    const out = [];
    let prev = '';
    for (const p of parts) {
      const n = p.trim().toLowerCase();
      if (n && n === prev) continue;
      out.push(p);
      prev = n;
    }
    t = out.join(' ').trim();
  }
  return t.trim();
}

const estTokens = (s) => Math.max(0, Math.round((s || '').length / 4));

export default function Connect() {
  const [prompt, setPrompt] = useState(SAMPLE_PROMPT);
  const [monthlyCalls, setMonthlyCalls] = useState(100_000);
  const [avgCostPerCall, setAvgCostPerCall] = useState(0.025); // $0.025 = typical gpt-5.2 call

  const compacted = useMemo(() => clientCompact(prompt), [prompt]);
  const origTok = useMemo(() => estTokens(prompt), [prompt]);
  const newTok = useMemo(() => estTokens(compacted), [compacted]);
  const compactSavingsPct = origTok > 0 ? Math.max(0, ((origTok - newTok) / origTok)) : 0;

  // Conservative blended estimate: compact 12% + cache 18% + cascade 10% on opt-in mix
  const cacheEstimatePct = 0.18;
  const cascadeEstimatePct = 0.10;
  const blendedSavingsPct = Math.min(0.55, compactSavingsPct * 0.6 + cacheEstimatePct + cascadeEstimatePct);

  const monthlyBillUSD = monthlyCalls * avgCostPerCall;
  const monthlySavedUSD = monthlyBillUSD * blendedSavingsPct;
  const annualSavedUSD = monthlySavedUSD * 12;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Helmet>
        <title>Connect · Auto-Compiler Proxy · CARTIE</title>
        <meta name="description" content="Drop-in OpenAI / Anthropic / Gemini / Groq / Mistral proxy. Compact prompts, cache duplicates, cascade to cheaper models. 20-50% savings on typical prompts, up to 70% on repetitive workloads — no code changes." />
      </Helmet>

      {/* Header */}
      <header className="border-b border-white/5 px-4 py-3 flex items-center justify-between sticky top-0 z-30 bg-slate-950/90 backdrop-blur" data-testid="connect-header">
        <Link to="/" className="text-sm font-bold text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="connect-logo">
          ← CARTIE
        </Link>
        <Link
          to="/login"
          className="text-xs font-semibold text-slate-400 hover:text-emerald-300 transition-colors"
          data-testid="connect-signin"
        >
          Sign in
        </Link>
      </header>

      {/* Hero */}
      <section className="px-4 py-12 sm:py-16 max-w-5xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest mb-5" data-testid="connect-badge">
          <Sparkles className="w-3 h-3" /> Auto-Compiler Proxy · 60-Second Connect
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]" data-testid="connect-h1">
          Point your OpenAI SDK at CARTIE.<br />
          <span className="text-emerald-400">Pay 20-50% less on the next call.</span>
        </h1>
        <p className="mt-5 text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed">
          Drop-in proxy for OpenAI, Anthropic, Gemini, Groq, and Mistral. Same response shape. Three savings layers
          (compaction · cache · cascade) baked in. Headers tell you exactly what we saved on every call.
        </p>
      </section>

      {/* Step 1 — Live demo */}
      <section className="px-4 py-10 border-y border-white/5 bg-slate-900/40">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center text-slate-950 font-extrabold shadow-md shadow-emerald-500/40" data-testid="step-1-badge">1</div>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-emerald-300">Try it · no signup</div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">Watch us shrink a real prompt.</h2>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-white/10 bg-slate-950/80 p-4" data-testid="demo-input-card">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Your prompt</div>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={9}
                className="w-full bg-slate-900 border border-white/10 rounded-md p-3 text-[12px] text-slate-200 font-mono leading-relaxed focus:border-emerald-500/50 focus:outline-none"
                data-testid="demo-prompt-input"
              />
            </div>
            <div className="rounded-2xl border border-emerald-500/30 bg-slate-950 bg-gradient-to-br from-emerald-500/5 via-slate-950 to-slate-950 p-4" data-testid="demo-output-card">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2">After compaction</div>
              <pre className="bg-slate-900 border border-white/10 rounded-md p-3 text-[12px] text-emerald-200 font-mono leading-relaxed h-[220px] overflow-auto whitespace-pre-wrap" data-testid="demo-compacted-output">{compacted}</pre>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <Pill label="Original" value={`${origTok} tok`} accent="slate" testid="demo-pill-original" />
                <Pill label="Compacted" value={`${newTok} tok`} accent="emerald" testid="demo-pill-compacted" />
                <Pill
                  label="Saved"
                  value={`${Math.round(compactSavingsPct * 100)}%`}
                  accent="emerald-strong"
                  testid="demo-pill-saved"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Step 2 — Calculator */}
      <section className="px-4 py-10 border-b border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center text-slate-950 font-extrabold shadow-md shadow-emerald-500/40" data-testid="step-2-badge">2</div>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-emerald-300">Your bill, with CARTIE</div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">What this saves you at your volume.</h2>
            </div>
          </div>
          <div className="grid lg:grid-cols-[1fr_auto] gap-5 items-center">
            <div className="rounded-2xl border border-white/10 bg-slate-950 p-5 space-y-3" data-testid="calc-inputs">
              <NumberRow
                label="LLM calls / month"
                value={monthlyCalls}
                onChange={setMonthlyCalls}
                min={1000} max={100_000_000} step={10_000}
                testid="calc-calls"
              />
              <NumberRow
                label="Avg $ / call"
                value={avgCostPerCall}
                onChange={setAvgCostPerCall}
                min={0.0005} max={1} step={0.005}
                isCurrency
                testid="calc-cost"
              />
              <div className="pt-2 border-t border-white/10 text-xs text-slate-400">
                Blended savings estimate: <span className="text-emerald-300 font-mono font-bold">{Math.round(blendedSavingsPct * 100)}%</span>
                {' '}(compaction <span className="font-mono">{Math.round(compactSavingsPct * 60)}%</span> · cache ~18% · cascade ~10%).
                {' '}Heavy batch workloads see higher; chat apps see lower. We&apos;ll show your real numbers on day 1.
              </div>
            </div>
            <div className="rounded-2xl border border-emerald-500/40 bg-slate-950 bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-slate-950 p-5 sm:p-6 lg:min-w-[280px]" data-testid="calc-output">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1">You save</div>
              <div className="text-3xl sm:text-4xl font-extrabold text-emerald-300 font-mono tabular-nums" data-testid="calc-monthly">
                ${monthlySavedUSD.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
              <div className="text-xs text-slate-400 mt-1">per month at this volume</div>
              <div className="mt-3 pt-3 border-t border-emerald-500/20 text-xs text-slate-400">
                <span className="text-white font-bold font-mono" data-testid="calc-annual">${annualSavedUSD.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span> / year ·
                vs. monthly bill of <span className="font-mono">${monthlyBillUSD.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Step 3 — The 1-line install */}
      <section className="px-4 py-10 border-b border-white/5 bg-slate-900/40">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center text-slate-950 font-extrabold shadow-md shadow-emerald-500/40" data-testid="step-3-badge">3</div>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-emerald-300">Drop-in install · 1 line of code</div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">Same SDK. New <code className="bg-slate-950 text-emerald-300 px-1.5 py-0.5 rounded text-base">base_url</code>.</h2>
            </div>
          </div>
          <div className="rounded-2xl border border-emerald-500/20 bg-slate-950 p-5 overflow-x-auto" data-testid="install-snippet-card">
            <pre className="text-[12px] sm:text-[13px] font-mono text-emerald-200 leading-relaxed">{`from openai import OpenAI

client = OpenAI(
    api_key="<YOUR-OPENAI-KEY>",
    base_url="https://cartieai.com/api/llm-proxy/v1",
    default_headers={"X-Cartie-Key": "<YOUR-CARTIE-PROXY-KEY>"},
)

# Every call: compacted → cached → cascaded.
# Returns standard OpenAI response.
resp = client.chat.completions.create(
    model="gpt-5.2",
    messages=[{"role": "user", "content": "Hello"}],
)
# Response headers tell you exactly what we saved:
#   X-Cartie-Tokens-Saved, X-Cartie-Cache-Hit, X-Cartie-Saved-Usd`}</pre>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Link
              to="/signup?intent=proxy"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm transition-all hover:-translate-y-0.5 shadow-lg shadow-emerald-500/30"
              data-testid="connect-cta-signup"
            >
              Get my proxy key · free <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/engine-27"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-white/15 hover:border-emerald-500/40 text-white text-sm font-semibold transition-all"
              data-testid="connect-cta-docs"
            >
              Read the full engine docs
            </Link>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="px-4 py-10 border-b border-white/5">
        <div className="max-w-5xl mx-auto grid sm:grid-cols-3 gap-4">
          <TrustItem
            icon={Lock}
            title="Your provider keys are never stored"
            body="We forward them on every call. We don't log them, hash them only for identification, never persist them at rest."
            testid="trust-keys"
          />
          <TrustItem
            icon={Code2}
            title="Standard OpenAI / Anthropic / Gemini / Groq / Mistral responses"
            body="No new SDK to learn. Compatible with the existing openai, anthropic, and langchain libraries you already use — all five providers are OpenAI-compatible."
            testid="trust-sdk"
          />
          <TrustItem
            icon={CheckCircle2}
            title="3× ROI guarantee · contractually binding"
            body="If we don't save you 3× our subscription fee in your first 90 days, you get a full refund. In writing."
            testid="trust-guarantee"
          />
        </div>
      </section>

      <footer className="px-4 py-6 text-center text-xs text-slate-500">
        The Auto-Compiler Proxy is one of <Link to="/patent-portfolio" className="text-emerald-400 hover:underline">{ENGINE_COUNT} CARTIE engines</Link> ·
        {' '}<Link to="/security" className="text-emerald-400 hover:underline">Security</Link> ·
        {' '}<Link to="/guarantee" className="text-emerald-400 hover:underline">3× ROI guarantee</Link>
      </footer>
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────
function Pill({ label, value, accent, testid }) {
  const cls = {
    slate: 'bg-slate-900 border-white/10 text-slate-300',
    emerald: 'bg-slate-900 border-white/10 text-emerald-300',
    'emerald-strong': 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300',
  }[accent];
  return (
    <div className={`rounded-md border px-2 py-2 text-center ${cls}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-base font-bold font-mono tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function NumberRow({ label, value, onChange, min, max, step, isCurrency, testid }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-400 mb-1">{label}</div>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 accent-emerald-400"
          data-testid={testid}
        />
        <div className="text-sm font-mono font-bold text-emerald-300 w-28 text-right tabular-nums" data-testid={`${testid}-value`}>
          {isCurrency
            ? `$${Number(value).toFixed(3)}`
            : Number(value).toLocaleString()}
        </div>
      </div>
    </label>
  );
}

function TrustItem({ icon: Icon, title, body, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/60 p-4" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-300 mb-2">
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-sm font-bold text-white">{title}</div>
      <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">{body}</div>
    </div>
  );
}
